import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Lock,
  Loader2,
  Eye,
  EyeOff,
  CheckCircle2,
  Shield,
  Check,
  Circle,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const PASSWORD_RULES = [
  { id: "length", label: "Mínimo 8 caracteres", test: (pw) => pw.length >= 8 },
  { id: "upper", label: "Una mayúscula", test: (pw) => /[A-Z]/.test(pw) },
  { id: "lower", label: "Una minúscula", test: (pw) => /[a-z]/.test(pw) },
  {
    id: "special",
    label: "Un número o símbolo",
    test: (pw) => /[0-9!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'/`~]/.test(pw),
  },
];

export default function PasswordSetupPrompt({ studentHasPassword, onComplete }) {
  const { updatePassword } = useAuth();
  const [visible, setVisible] = useState(false);
  const [show, setShow] = useState(false);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (studentHasPassword) return;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => setVisible(true));
    return () => { document.body.style.overflow = ""; };
  }, [studentHasPassword]);

  if (studentHasPassword) return null;

  const close = () => {
    setVisible(false);
    setTimeout(onComplete, 250);
  };

  const passedRules = PASSWORD_RULES.filter((r) => r.test(pw));
  const allRulesPassed = passedRules.length === PASSWORD_RULES.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!allRulesPassed) {
      setError("La contraseña no cumple todos los requisitos");
      return;
    }
    if (pw !== confirm) {
      setError("Las contraseñas no coinciden");
      return;
    }

    setLoading(true);
    try {
      await updatePassword(pw);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    close();
  };

  const handleSuccessClose = () => {
    onComplete();
  };

  const content = (
    <div className="fixed inset-0 z-[99999]">
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity duration-250 ${visible ? "opacity-100" : "opacity-0"}`}
      />

      <div
        className={`
          fixed inset-0 flex items-center justify-center p-4
          transition-all duration-250
          ${visible ? "opacity-100 scale-100" : "opacity-0 scale-95"}
        `}
      >
        <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
          {success ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-emerald-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                ¡Contraseña configurada!
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Ahora puedes acceder al portal usando tu email y contraseña.
              </p>
              <button
                onClick={handleSuccessClose}
                className="px-6 py-3 bg-brand hover:bg-brand-dark text-white rounded-xl text-sm font-semibold transition-all"
              >
                Continuar al portal
              </button>
            </div>
          ) : (
            <>
              <div className="bg-gradient-to-r from-brand to-brand-dark p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Shield size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">¡Bienvenido/a!</h3>
                    <p className="text-sm text-white/80">
                      Configura tu contraseña para acceder más fácilmente
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <p className="text-sm text-gray-600">
                  Has entrado con un enlace mágico. Te recomendamos configurar una contraseña 
                  para poder acceder más rápido en el futuro.
                </p>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">
                    Nueva contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={show ? "text" : "password"}
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      placeholder="Introduce tu contraseña"
                      className="w-full px-4 py-3 pr-10 border-[1.5px] border-gray-200 rounded-xl text-sm bg-gray-50 outline-none transition-all focus:border-brand focus:bg-white focus:ring-3 focus:ring-brand/10"
                    />
                    <button
                      type="button"
                      onClick={() => setShow(!show)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {show ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  
                  {pw.length > 0 && (
                    <div className="mt-2 p-2.5 bg-gray-50 rounded-lg space-y-1.5">
                      {PASSWORD_RULES.map((rule) => {
                        const passed = rule.test(pw);
                        return (
                          <div
                            key={rule.id}
                            className={`flex items-center gap-2 text-xs transition-colors ${
                              passed ? "text-emerald-600" : "text-gray-400"
                            }`}
                          >
                            {passed ? (
                              <Check size={12} className="shrink-0" />
                            ) : (
                              <Circle size={12} className="shrink-0" />
                            )}
                            {rule.label}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">
                    Confirmar contraseña
                  </label>
                  <input
                    type={show ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repite la contraseña"
                    className="w-full px-4 py-3 border-[1.5px] border-gray-200 rounded-xl text-sm bg-gray-50 outline-none transition-all focus:border-brand focus:bg-white focus:ring-3 focus:ring-brand/10"
                  />
                  {confirm.length > 0 && pw !== confirm && (
                    <p className="mt-1.5 text-xs text-amber-600">
                      Las contraseñas no coinciden
                    </p>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleSkip}
                    className="flex-1 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Más tarde
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !allRulesPassed || pw !== confirm}
                    className="flex-1 py-3 bg-brand hover:bg-brand-dark text-white rounded-xl text-sm font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Lock size={16} />
                        Configurar
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
